"use client";

import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import StepMarker from "./StepMarker";
import { Button } from "./Button";
import LocationPicker from "./LocationPicker";
import { fetchMasterCities } from "../../../services/locationServices";

// All US States
const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
  { code: "DC", name: "District of Columbia" },
];

export default function Step3BusinessAddress({
  formData,
  updateFormData,
  setLocationPlaceId,
  onBack,
  onNext,
}) {
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  useEffect(() => {
    const loadCities = async () => {
      try {
        setLoadingCities(true);
        const response = await fetchMasterCities();
        if (response && response.data) {
          setCities(response.data);
        }
      } catch (error) {
        console.error("Failed to load cities:", error);
      } finally {
        setLoadingCities(false);
      }
    };

    loadCities();
  }, []);

  const handleChange = (e) => {
    updateFormData({ [e.target.name]: e.target.value });
  };

  const handleLocationChange = (location) => {
    updateFormData({
      businesslocationmap: { lat: location.lat, lng: location.lng },
      venue: location.address,
    });
  };

  const isValid =
    formData.businessaddress1 &&
    formData.businesscity &&
    formData.businessstate &&
    formData.businesslocationmap;

  return (
    <div>
      <StepMarker currentStep={3} totalSteps={5} onBack={onBack} />

      <h2 className="text-2xl font-bold mb-1">Business Address</h2>
      <p className="text-gray-500 mb-6">Where is your business located?</p>

      <div className="space-y-4">
        <div>
          <input
            id="businessaddress1"
            name="businessaddress1"
            value={formData.businessaddress1 || ""}
            onChange={handleChange}
            placeholder="Address Line 1*"
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent"
            required
          />
        </div>

        <div>
          <input
            id="businessaddress2"
            name="businessaddress2"
            value={formData.businessaddress2 || ""}
            onChange={handleChange}
            placeholder="Address Line 2 (Optional)"
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent"
          />
        </div>

        <div>
          <input
            id="businessaddress3"
            name="businessaddress3"
            value={formData.businessaddress3 || ""}
            onChange={handleChange}
            placeholder="Address Line 3 (Optional)"
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="relative">
            <select
              id="businesscity"
              name="businesscity"
              value={formData.businesscity || ""}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent pr-10 cursor-pointer appearance-none bg-white"
              required
              disabled={loadingCities}
            >
              <option value="" disabled>
                {loadingCities ? "Loading cities..." : "Select City*"}
              </option>
              {cities.map((cityData) => (
                <option key={cityData.id} value={cityData.city}>
                  {cityData.city}
                </option>
              ))}
            </select>
            <ChevronDown
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
              size={20}
            />
          </div>

          <div className="relative">
            <select
              id="businessstate"
              name="businessstate"
              value={formData.businessstate || ""}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent pr-10 cursor-pointer appearance-none bg-white"
              required
            >
              <option value="" disabled>
                Select State*
              </option>
              {US_STATES.map((state) => (
                <option key={state.code} value={state.name}>
                  {state.name}
                </option>
              ))}
            </select>
            <ChevronDown
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
              size={20}
            />
          </div>
        </div>

        <div>
          <div className="uppercase text-xs font-medium text-gray-500 mb-2">
            BUSINESS LOCATION ON MAP*
          </div>
          <LocationPicker
            setLocationPlaceId={setLocationPlaceId}
            value={
              formData.businesslocationmap
                ? {
                    lat: formData.businesslocationmap.lat,
                    lng: formData.businesslocationmap.lng,
                    address: formData.venue || "",
                  }
                : undefined
            }
            onChange={handleLocationChange}
          />
          {formData.businesslocationmap && (
            <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="text-sm text-green-800">
                <strong>Selected Location:</strong>
              </div>
              <div className="text-sm text-green-700 mt-1">
                {formData.venue}
              </div>
              <div className="text-xs text-green-600 mt-1">
                Coordinates: {formData.businesslocationmap.lat.toFixed(6)},{" "}
                {formData.businesslocationmap.lng.toFixed(6)}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button onClick={onNext} disabled={!isValid} className="px-8">
          Continue
        </Button>
      </div>
    </div>
  );
}
